import { z } from 'zod';
import { CrmLeadStatusSchema } from './crm';

/**
 * Contrato da tela **Tarefas** do CRM (`/tasks`): a fila de execução das
 * tarefas dos leads, de todos os quadros em escopo.
 *
 * Origem: #782 — 2026-09-29 13:01 +0200 (menu Hunter/Farmer + tela Tarefas).
 *
 * Por quê: a tarefa mora no QUADRO (`crm_boards/{boardId}/crm_tasks/{taskId}`,
 * `CrmTaskSchema` em `./crm`) e até a #782 só aparecia na aba do lead. As
 * rules não têm regra de collection group para `crm_tasks`, então a lista
 * cruzada vem de uma callable (`crmListTasks`), com o escopo por papel do
 * `fetchLeadsInScope` — o mesmo desenho do `crmListAppointments`.
 *
 * Usado por: callable `crmListTasks` (`apps/crm-functions/src/crmTasks.ts`);
 * web `apps/crm` — `useTaskRows` e `TasksPage` (`features/tasks/`).
 *
 * Issues: #782.
 */

/**
 * Status pedido na listagem. `PENDING` = fila de execução; `DONE` = concluídas
 * nos últimos {@link CRM_TASKS_DONE_WINDOW_DAYS} dias (a lista de concluídas só
 * cresce — sem janela, a leitura cresceria para sempre).
 */
export const CrmTaskListStatusSchema = z.enum(['PENDING', 'DONE']);
export type CrmTaskListStatus = z.infer<typeof CrmTaskListStatusSchema>;
export const CrmTaskListStatus = CrmTaskListStatusSchema.enum;

/** Janela das concluídas (dias), contada do relógio do servidor. */
export const CRM_TASKS_DONE_WINDOW_DAYS = 30;

export const CrmListTasksInputSchema = z.object({
  status: CrmTaskListStatusSchema.default('PENDING'),
});
export type CrmListTasksInput = z.infer<typeof CrmListTasksInputSchema>;

/**
 * Linha da tela Tarefas: a tarefa com o lead e o dono já resolvidos.
 *
 * Dados — exemplo (anonimizado):
 *   {
 *     "boardId": "board-1", "leadId": "lead-1", "taskId": "task-1",
 *     "title": "Ligar para confirmar a demo",
 *     "dueAt": 1790700000000,          // epoch ms; null = sem prazo
 *     "done": false, "doneAt": null,   // doneAt: epoch ms gravado pelo cliente (toggleLeadTask)
 *     "createdAt": 1790600000000,
 *     "name": "Maria", "companyName": "Restaurante Exemplo",
 *     "leadStatus": "OPEN",            // tarefa de lead ganho continua sendo trabalho
 *     "ownerUserId": "uid-1", "ownerManagerUserId": "uid-9",
 *     "ownerName": "Vendedor", "ownerPhotoPath": null
 *   }
 *
 * "Dono" da tarefa = dono do LEAD (`ownerUserId`): `assigneeUid` existe no
 * schema mas é sempre `null` hoje (`addLeadTask`). `ownerManagerUserId` é o
 * gerente direto do dono — base do `TeamFilter`, como nas outras listas.
 */
export const CrmTaskRowSchema = z.object({
  boardId: z.string(),
  leadId: z.string(),
  taskId: z.string(),
  title: z.string(),
  dueAt: z.number().int().nullable(),
  done: z.boolean(),
  doneAt: z.number().int().nullable(),
  createdAt: z.number().int().nullable(),
  name: z.string(),
  companyName: z.string().nullable(),
  leadStatus: CrmLeadStatusSchema,
  ownerUserId: z.string(),
  ownerManagerUserId: z.string().nullable(),
  ownerName: z.string().nullable(),
  ownerPhotoPath: z.string().nullable(),
});
export type CrmTaskRow = z.infer<typeof CrmTaskRowSchema>;

type Orderable = Pick<CrmTaskRow, 'dueAt' | 'done' | 'doneAt' | 'createdAt'>;

/**
 * Comparador da **ordem de execução**, para `Array.prototype.sort`.
 *
 * Por quê: a tela é uma fila — o que fazer primeiro vem em cima. Pendentes:
 * com prazo por `dueAt` crescente (as atrasadas, que têm o menor prazo, ficam
 * naturalmente no topo), e as sem prazo no FIM, por `createdAt` crescente
 * (a mais antiga esperando primeiro). Concluídas: `doneAt` decrescente (a mais
 * recente em cima). Desempate por `createdAt`, para a ordem ser estável entre
 * recargas.
 *
 * Contrato: puro; não lê relógio. Usado pelo servidor (`crmListTasks`) — o
 * cliente exibe na ordem recebida.
 *
 * Dicas: não trocar `null` por `Infinity`/`0` no dado — o `null` é o contrato
 * ("sem prazo") que a tela exibe como "—".
 */
export function compareCrmTaskExecutionOrder(a: Orderable, b: Orderable): number {
  if (a.done !== b.done) return a.done ? 1 : -1;
  if (a.done) {
    const byDone = (b.doneAt ?? 0) - (a.doneAt ?? 0);
    if (byDone !== 0) return byDone;
  } else {
    if (a.dueAt == null && b.dueAt != null) return 1;
    if (a.dueAt != null && b.dueAt == null) return -1;
    if (a.dueAt != null && b.dueAt != null && a.dueAt !== b.dueAt) return a.dueAt - b.dueAt;
  }
  return (a.createdAt ?? 0) - (b.createdAt ?? 0);
}
